import {ImageType, MAX_IMAGE_HEIGHT, MAX_IMAGE_WIDTH, MAX_UPLOAD_BYTES, UPLOAD_IMAGE_TYPES} from '../vocabulary.js';

/** Bytes in a megabyte, as the upload limit counts them. */
const BYTES_PER_MB = 1024 * 1024;

/**
 * A file size in megabytes with one decimal, rounded up so a file over the limit never reads
 * as the limit itself (10.04 MB shows as 10.1, not 10.0).
 * @param {number} bytes
 * @returns {string}
 */
function megabytes(bytes) {
    return (Math.ceil((bytes / BYTES_PER_MB) * 10) / 10).toFixed(1);
}

/**
 * Why the backend would refuse a chosen file, checked before the form is sent so the user
 * hears it right away. The backend converts every upload with an image content type, and it
 * can only convert JPEG, PNG, GIF and WebP: an image field takes those four only, and a
 * document field takes any file except an image in another format (such as SVG, HEIC, TIFF or
 * BMP). Any file is at most 10 MB. Returns a translation key with its template values, or
 * null when the file is fine.
 * @param {{name: string, type: string, size: number}} file
 * @param {{image: boolean}} options `image` for an image field, false for a document
 * @returns {{key: string, vars: {name: string, size?: string}} | null}
 */
export function uploadProblem(file, {image}) {
    const convertible = UPLOAD_IMAGE_TYPES.includes(file.type);
    if (image && !convertible) {
        return {key: 'pageForm.uploadWrongType', vars: {name: file.name}};
    }
    if (!image && !convertible && (file.type || '').toLowerCase().startsWith('image/')) {
        return {key: 'pageForm.uploadDocumentImageType', vars: {name: file.name}};
    }
    if (file.size > MAX_UPLOAD_BYTES) {
        return {key: 'pageForm.uploadTooLarge', vars: {name: file.name, size: megabytes(file.size)}};
    }
    return null;
}

/**
 * Whether the browser prepares a chosen image of this content type before it is uploaded (see
 * {@link imageTarget}): JPEG and PNG only.
 * @param {string} type
 * @returns {boolean}
 */
export function isPreparableImageType(type) {
    return type === ImageType.JPEG || type === ImageType.PNG;
}

/** EXIF tag number of Orientation. */
const ORIENTATION_TAG = 0x0112;

/** How many leading bytes of a JPEG are read for its EXIF block, which sits before the image data. */
export const EXIF_SCAN_BYTES = 128 * 1024;

/**
 * The EXIF orientation (1 to 8) of a JPEG from its leading bytes, or 1 (upright) when it has
 * none, is not a JPEG, or its EXIF block cannot be read within the bytes given.
 * @param {Uint8Array} bytes
 * @returns {number}
 */
export function jpegOrientation(bytes) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.length < 4 || view.getUint16(0) !== 0xffd8) return 1;
    let offset = 2;
    while (offset + 4 <= bytes.length) {
        const marker = view.getUint16(offset);
        // Metadata segments all come before the start of scan (FFDA).
        if ((marker & 0xff00) !== 0xff00 || marker === 0xffda) return 1;
        const length = view.getUint16(offset + 2);
        if (length < 2) return 1;
        if (marker === 0xffe1 && offset + 2 + length <= bytes.length) {
            const orientation = exifOrientation(view, offset + 4, length - 2);
            if (orientation !== null) return orientation;
        }
        offset += 2 + length;
    }
    return 1;
}

/**
 * The orientation in one APP1 segment's payload, or null when the segment is not an Exif block
 * holding an orientation (an XMP block, say).
 * @param {DataView} view
 * @param {number} start offset of the payload
 * @param {number} length length of the payload
 * @returns {number|null}
 */
function exifOrientation(view, start, length) {
    const end = start + length;
    // "Exif" followed by two zero bytes, then a TIFF header of at least 8 bytes.
    if (length < 14 || view.getUint32(start) !== 0x45786966 || view.getUint16(start + 4) !== 0) return null;
    const tiff = start + 6;
    const byteOrder = view.getUint16(tiff);
    if (byteOrder !== 0x4949 && byteOrder !== 0x4d4d) return null;
    const littleEndian = byteOrder === 0x4949;
    const ifd = tiff + view.getUint32(tiff + 4, littleEndian);
    if (ifd + 2 > end) return null;
    const entries = view.getUint16(ifd, littleEndian);
    for (let index = 0; index < entries; index++) {
        const entry = ifd + 2 + index * 12;
        if (entry + 12 > end) return null;
        if (view.getUint16(entry, littleEndian) === ORIENTATION_TAG) {
            const value = view.getUint16(entry + 8, littleEndian);
            return value >= 1 && value <= 8 ? value : 1;
        }
    }
    return null;
}

/**
 * The size the browser re-encodes a chosen image to, or null when it is uploaded as it is.
 * A JPEG or PNG is re-encoded when it is larger than the backend publishes, a JPEG also when it
 * is over the upload limit or its EXIF orientation turns it (the backend's encoder ignores EXIF,
 * so the photo would show sideways). A PNG that already fits is not re-encoded for its file
 * size: the canvas writes PNG losslessly, so the file would hardly shrink, and the upload limit
 * refuses it right away instead. Any other image is uploaded as it is, so it is not compressed
 * twice; the backend converts it once. GIF and WebP are never re-encoded. An image
 * is scaled down to fit {@link MAX_IMAGE_WIDTH} x {@link MAX_IMAGE_HEIGHT} with its aspect
 * ratio kept, rounded like the backend's resize, and never scaled up.
 * @param {{type: string, width: number, height: number, size: number, orientation?: number}} image
 *     `orientation` is the JPEG's EXIF orientation, 1 (upright) when absent
 * @returns {{width: number, height: number} | null}
 */
export function imageTarget({type, width, height, size, orientation = 1}) {
    const oversized = width > MAX_IMAGE_WIDTH || height > MAX_IMAGE_HEIGHT;
    const reencode = (type === ImageType.PNG && oversized)
        || (type === ImageType.JPEG && (oversized || size > MAX_UPLOAD_BYTES || orientation !== 1));
    if (!reencode) return null;
    if (!oversized) return {width, height};
    const scale = Math.min(MAX_IMAGE_WIDTH / width, MAX_IMAGE_HEIGHT / height);
    return {width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale))};
}
